import { useEffect, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";

import { Notification } from "@stellar/design-system";

import { QueryStatusHandler } from "@/components/QueryStatusHandler";

import { MULTISIG_SIGNER_STATUS } from "@/constants/multisigEnrollment";
import { Routes } from "@/constants/settings";

import {
  useMultisigUnenrollmentCancel,
  useMultisigUnenrollmentInitiate,
  useUnenrollmentStatus,
} from "@/apiQueries/multisigUnenrollment";
import { useUsers } from "@/apiQueries/useUsers";

import { formatUnenrollmentDeadline } from "@/helpers/unenrollmentDeadline";
import {
  getUnenrollmentErrorMessage,
  UNENROLLMENT_ERROR_RULES,
} from "@/helpers/unenrollmentErrors";
import type { UnenrollmentErrorCode } from "@/helpers/unenrollmentErrors";
import { recordUnenrollmentOutcome } from "@/helpers/unenrollmentOutcome";
import { formatActiveSignerName } from "@/helpers/unenrollmentView";
import type {
  UnenrollmentSignRequest,
  UnenrollmentUnavailableReason,
  UnenrollmentView,
} from "@/helpers/unenrollmentView";

import { useRedux } from "@/hooks/useRedux";

import { ManageCoSigning } from "./components/ManageCoSigning";
import { UnenrollmentDisbursement, UnenrollmentReview } from "./components/UnenrollmentReview";
import { UnenrollmentPerspective, UnenrollmentSignModal } from "./components/UnenrollmentSignModal";
import { UnenrollmentWarning } from "./components/UnenrollmentWarning";
import { useUnenrollmentSign } from "./hooks/useUnenrollmentSign";

import type {
  ApiUser,
  MultisigUnenrollmentInitiateResponse,
  MultisigUnenrollmentStatus,
} from "@/types";

type View = "manage" | "warning" | "review";

// The backend returns `pending_disbursements: []` and has no shape for them yet.
const PENDING_DISBURSEMENTS: UnenrollmentDisbursement[] = [];

// Preflight reasons reuse the backend error copy for the same rule.
const UNAVAILABLE_ERROR_CODE: Record<UnenrollmentUnavailableReason, UnenrollmentErrorCode> = {
  not_owner: "OWNER_REQUIRED",
  not_enrolled: "NOT_ENROLLED",
  quorum_unreachable: "QUORUM_UNREACHABLE",
};

const unavailableMessage = (reason: UnenrollmentUnavailableReason) =>
  UNENROLLMENT_ERROR_RULES.find((rule) => rule.code === UNAVAILABLE_ERROR_CODE[reason])?.message ??
  null;

type InProgressView = Extract<UnenrollmentView, { kind: "in-progress" }>;

/** The other Owner who signed, named from the roster, for the review's second signature row. */
const secondSignerName = (view: InProgressView): string | undefined => {
  const mine = { userId: view.me.userId, key: view.me.signer?.stellar_public_key };
  const theirs = (view.tx.signatures ?? []).find(
    (s) => s.signer_user_id !== mine.userId && s.signer_public_key !== mine.key,
  );
  if (!theirs) return undefined;
  const signer = view.activeSigners.find(
    (a) => a.user_id === theirs.signer_user_id || a.stellar_public_key === theirs.signer_public_key,
  );
  return signer ? formatActiveSignerName(signer) : undefined;
};

/**
 * A removal that failed on the network has no Settings banner in the design,
 * so it is reported here, where the Owner can start again.
 */
const terminalNotice = (view: UnenrollmentView) => {
  if (view.kind !== "failed") return null;
  return (
    <Notification variant="error" title="Turning off co-signing failed" isFilled>
      The Stellar network rejected the change, so co-signing stays on. Try again, and contact
      support if it keeps failing.
    </Notification>
  );
};

const SignDialog = ({
  userId,
  signRequest,
  coSignerCount,
  disbursementCount,
  perspective,
  deadline,
  onSigned,
  onClose,
}: {
  userId: string;
  signRequest: UnenrollmentSignRequest;
  coSignerCount: number;
  disbursementCount: number;
  perspective: UnenrollmentPerspective;
  deadline: string | null;
  onSigned: (status: MultisigUnenrollmentStatus) => void;
  onClose: () => void;
}) => {
  const { stage, uri, signerPublicKey, error, result } = useUnenrollmentSign({
    userId,
    txId: signRequest.txId,
    unsignedXdr: signRequest.unsignedXdr,
  });
  // A dialog signs at most once, so report the submitted status once per
  // mount, however often the parent re-renders or the hook re-creates it.
  const reported = useRef(false);

  useEffect(() => {
    if (stage !== "signed" || !result || reported.current) return;
    reported.current = true;
    onSigned(result);
  }, [stage, result, onSigned]);

  return (
    <UnenrollmentSignModal
      visible
      stage={stage}
      uri={uri}
      signerPublicKey={signerPublicKey}
      coSignerCount={coSignerCount}
      disbursementCount={disbursementCount}
      perspective={perspective}
      deadline={deadline}
      error={error}
      onCancel={onClose}
      onDone={onClose}
    />
  );
};

export const MultisigManage = () => {
  const navigate = useNavigate();
  const { profile } = useRedux("profile");
  const { data: users, error: usersError } = useUsers();
  const [view, setView] = useState<View>("manage");
  // The initiate response, which stands in for the removal until the GET refetch lands.
  const [tx, setTx] = useState<MultisigUnenrollmentInitiateResponse | null>(null);
  // What the open dialog signs, kept as opened so a refetch can't swap or close it.
  const [openSignRequest, setOpenSignRequest] = useState<UnenrollmentSignRequest | null>(null);
  // The status a signature or cancel mutation returned. A finished removal
  // disappears from the GET, so this is what keeps its outcome on screen.
  const [lastKnownTx, setLastKnownTx] = useState<MultisigUnenrollmentStatus | null>(null);
  // What the removal looked like while pending. Once it completes the GET
  // stops serving it and the backend drops every co-signer, and the receipt
  // still has to list them and name who signed.
  const [snapshot, setSnapshot] = useState<{
    coSigners: ApiUser[];
    perspective: UnenrollmentPerspective;
    initiatorName?: string;
    otherSignerName?: string;
  } | null>(null);

  const initiate = useMultisigUnenrollmentInitiate();
  const cancel = useMultisigUnenrollmentCancel();
  const {
    data: status,
    error: statusError,
    refetch: refetchStatus,
  } = useUnenrollmentStatus({
    userId: profile.data.id,
    role: profile.data.role ?? null,
    lastKnownTx,
  });

  const inProgress = status?.kind === "in-progress" ? status : null;
  const activeCoSigners = (users ?? []).filter(
    (u) => u.multisig_signer?.status === MULTISIG_SIGNER_STATUS.ACTIVE,
  );

  // Someone else's request, which this Owner responds to rather than owns.
  // A request this page just started is the Owner's own even before the GET
  // reports its initiator.
  const isResponder =
    !!inProgress && !tx && inProgress.initiatorUserId !== null && !inProgress.isInitiator;
  const perspective: UnenrollmentPerspective = isResponder ? "responder" : "initiator";
  const initiatorName = inProgress
    ? formatActiveSignerName(inProgress.initiator, "Another Owner")
    : undefined;
  const otherSignerName = inProgress ? secondSignerName(inProgress) : undefined;

  useEffect(() => {
    if (!inProgress || activeCoSigners.length === 0) return;
    setSnapshot({ coSigners: activeCoSigners, perspective, initiatorName, otherSignerName });
    // Re-snapshot whenever the pending roster or signatures change; the
    // length guards an empty intermediate render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inProgress?.tx.tx_id,
    inProgress?.tx.collected_signatures,
    perspective,
    activeCoSigners.map((u) => u.id).join("|"),
  ]);

  const goToSettings = () => navigate(`${Routes.SETTINGS}?tab=g`);

  // A request past its deadline can no longer be signed; the backend flips it
  // to EXPIRED on the next read. Settings shows the outcome banner.
  useEffect(() => {
    if (!inProgress?.isExpired) return;
    recordUnenrollmentOutcome({
      kind: "expired",
      txId: inProgress.tx.tx_id,
      actor: null,
      byMe: false,
    });
    goToSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress?.isExpired, inProgress?.tx.tx_id]);

  // The review lists co-signers from these queries, so wait for real data.
  if (!users || !status) {
    const error = usersError ?? statusError;
    return (
      <QueryStatusHandler isLoading={!error} isError={!!error} errorMessage={error?.message}>
        {null}
      </QueryStatusHandler>
    );
  }

  const myName = `${profile.data.firstName} ${profile.data.lastName}`.trim();
  const preflightError = status.kind === "unavailable" ? unavailableMessage(status.reason) : null;
  const signRequest: UnenrollmentSignRequest | null = inProgress
    ? inProgress.signRequest
    : tx
      ? { txId: tx.tx_id, unsignedXdr: tx.unsigned_xdr }
      : null;
  const activeTxId = inProgress?.tx.tx_id ?? tx?.tx_id ?? null;
  const deadlineLong = formatUnenrollmentDeadline(inProgress?.expiresAt, { style: "long" });
  const deadlineShort = formatUnenrollmentDeadline(inProgress?.expiresAt, { style: "short" });
  const coSigners = snapshot?.coSigners ?? activeCoSigners;

  if (status.kind === "completed") {
    return (
      <UnenrollmentReview
        currentUserId={profile.data.id}
        currentUserName={myName}
        coSigners={coSigners}
        disbursements={PENDING_DISBURSEMENTS}
        collectedSignatures={status.tx.required_signatures}
        requiredSignatures={status.tx.required_signatures}
        hasMySignature
        canSign={false}
        perspective={snapshot?.perspective ?? perspective}
        initiatorName={snapshot?.initiatorName ?? initiatorName}
        secondSignerName={snapshot?.otherSignerName ?? otherSignerName}
        isFinished
        onSign={() => undefined}
        onCancel={() => undefined}
        onExit={goToSettings}
      />
    );
  }

  const handleSigned = (submitted: MultisigUnenrollmentStatus) => {
    setLastKnownTx(submitted);
    // The mutation already invalidates the removal query; refetching here also
    // covers a signature reported without going through it.
    void refetchStatus();
  };

  const handleCancel = () => {
    if (!activeTxId) {
      setView("manage");
      return;
    }
    cancel.mutate(
      { txId: activeTxId },
      {
        onSuccess: (cancelled) => {
          recordUnenrollmentOutcome({
            kind: isResponder ? "rejected" : "cancelled",
            txId: cancelled.tx_id,
            actor: { userId: profile.data.id, name: myName },
            byMe: true,
          });
          setLastKnownTx(cancelled);
          setTx(null);
          setOpenSignRequest(null);
          // The outcome banner lives on Settings.
          goToSettings();
        },
      },
    );
  };

  if (view === "warning") {
    return (
      <UnenrollmentWarning
        isContinuing={initiate.isPending}
        canContinue={!preflightError}
        error={initiate.isError ? getUnenrollmentErrorMessage(initiate.error) : preflightError}
        onBack={() => setView("manage")}
        onCancel={() => setView("manage")}
        onContinue={() =>
          initiate.mutate(undefined, {
            onSuccess: (response) => {
              setTx(response);
              setView("review");
            },
          })
        }
      />
    );
  }

  // Any Owner with a request in flight lands on the review straight away:
  // the one who started it comes back to its status, the other responds.
  const showReview = view === "review" || !!inProgress;

  if (showReview && (inProgress || tx)) {
    return (
      <>
        <UnenrollmentReview
          currentUserId={profile.data.id}
          currentUserName={myName}
          coSigners={coSigners}
          disbursements={PENDING_DISBURSEMENTS}
          collectedSignatures={inProgress?.tx.collected_signatures ?? 0}
          requiredSignatures={inProgress?.tx.required_signatures}
          hasMySignature={inProgress?.hasMySignature ?? false}
          canSign={!!signRequest}
          perspective={perspective}
          initiatorName={initiatorName}
          secondSignerName={otherSignerName}
          deadline={deadlineLong}
          isCancelling={cancel.isPending}
          cancelError={cancel.isError ? getUnenrollmentErrorMessage(cancel.error) : null}
          onSign={() => setOpenSignRequest(signRequest)}
          onCancel={handleCancel}
          onExit={goToSettings}
        />
        {openSignRequest ? (
          <SignDialog
            userId={profile.data.id}
            signRequest={openSignRequest}
            coSignerCount={coSigners.length}
            disbursementCount={PENDING_DISBURSEMENTS.length}
            perspective={perspective}
            deadline={deadlineShort}
            onSigned={handleSigned}
            onClose={() => setOpenSignRequest(null)}
          />
        ) : null}
      </>
    );
  }

  return (
    <ManageCoSigning
      notice={terminalNotice(status)}
      onBack={goToSettings}
      onTurnOff={() => {
        initiate.reset();
        cancel.reset();
        // Starting over drops the previous removal's outcome from the screen.
        setLastKnownTx(null);
        // An Owner who reopens the page while a removal is pending goes to its review.
        setView(inProgress ? "review" : "warning");
      }}
    />
  );
};
